/// <amd-module name="VSS/Features/Search/ExpandableSearchBox/ExpandableSearchBox" />
import * as React from "react";
import * as Resources from "VSS/Features/Search/Resources";

import { ScreenSize } from "VSS/Core/Util/Screen";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { ISearchQuery, IVssSearchService } from "VSS/Platform/Search";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { contains } from "VSS/Platform/Util/DOM";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";

import { css } from "VSSUI/Util";

import { SearchInputComponent } from "../SearchInput";

interface IExpandableSearchBoxState {
    /**
     * True when the search input box has been opened/expanded.
     */
    isExpanded: boolean;

    /**
     * The Entity user has selected and searching in.
     */
    selectedProviderId: string | undefined;
}

/**
 * The ExpandableSearchBox is used to render the search input into the header.
 *
 * If the searchService has providers available on the page it will render the input
 * with the providers that have been defined.
 */
class ExpandableSearchBox extends VssComponent<{}, IExpandableSearchBoxState> {
    private _providersAvailable: boolean;
    private _searchHeader: HTMLDivElement | null;
    private _searchInputComponent: SearchInputComponent | undefined;
    private _isSearchSuggestionsEnabled: boolean = false;
    private _isHigherResolution: boolean = false;
    private _searchInputWidthOnFocus: number | undefined;
    private _project: boolean = false;

    constructor(props: {}, context: IVssComponentContext) {
        super(props);
        this.state = { isExpanded: false, selectedProviderId: undefined };

        const searchService = context.pageContext.getService<IVssSearchService>("IVssSearchService");
        this._providersAvailable = Object.keys(searchService.getProviders()).length > 0;
        this._isSearchSuggestionsEnabled = isFeatureEnabled(context.pageContext, "ms.vss-tfs-web.enable-search-suggestions", false);
        this._isHigherResolution = window.screen.availWidth >= 1920;

        // @NOTE: To avoid dependency issues we will check the TfsPageData for a project directly.
        //  This may be moved in the future to avoid this, but for now to unwind the dependencies
        //  I am moving it here.
        const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const tfsPageData = contributionService.getData("ms.vss-tfs-web.page-data");
        if (tfsPageData && (tfsPageData as any).project) {
            this._project = true;
        }
    }

    public render(): JSX.Element | null {
        const { isExpanded } = this.state;
        const instantSearchClassName = css(
            this._isSearchSuggestionsEnabled && "instant-search",
            this._isSearchSuggestionsEnabled && this._isHigherResolution ? "higher-resolution" : "lower-resolution"
        );

        return (
            <ScreenSizeConditional condition={screenSize => screenSize > ScreenSize.xsmall}>
                {this._providersAvailable ? (
                    <div
                        className="flex-row expandable-search-header"
                        ref={searchHeader => (this._searchHeader = searchHeader)}
                        onBlur={this._onBlur}
                        aria-owns={css(isExpanded && "searchBox-dropDown")}
                    >
                        {isExpanded ? (
                            <label className="search-context--label flex-row flex-center">
                                <span>{this._project ? Resources.ProjectLabel : Resources.AccountLabel}</span>
                            </label>
                        ) : null}
                        <SearchInputComponent
                            executeSearch={this._onExecuteSearch}
                            selectedProviderId={this.state.selectedProviderId}
                            dropdownGapSize={0}
                            helpClassName={css("expandable-header-search-help", instantSearchClassName)}
                            helpId="searchBox-dropDown"
                            showSearchProviders={false}
                            ariaLabel={Resources.SearchRoleAriaLabel}
                            placeholderText={Resources.SearchPlaceholder}
                            showSearchIcon={true}
                            spellCheck={false}
                            onInputFocus={this._expandSearchBox}
                            widthOnFocus={this.widthOnFocus}
                            maxLength={1024}
                            componentRef={(ref: SearchInputComponent) => {
                                this._searchInputComponent = ref;
                            }}
                            inputAriaLabel={Resources.SearchInputAriaLabel}
                        />
                    </div>
                ) : (
                    <div className="search-placeholder" />
                )}
            </ScreenSizeConditional>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.addEventListener(document.body, "searchTabLoaded", this._onSearchTabLoaded);
        this.registerShortcut();
        this._searchInputWidthOnFocus = undefined;
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this._dispatchEvent("searchCollapsed");
    }

    public componentDidUpdate(previousProps: {}, previousState: IExpandableSearchBoxState) {
        super.componentDidUpdate(previousProps, previousState);

        // Blurring the SearchInput once the search box collapses
        if (!this.state.isExpanded && this._searchInputComponent) {
            this._searchInputComponent.blur();
        }
    }

    public UNSAFE_componentWillReceiveProps(newProps: {}): void {
        const searchService = this.context.pageContext.getService<IVssSearchService>("IVssSearchService");
        this._providersAvailable = Object.keys(searchService.getProviders()).length > 0;
    }

    private get widthOnFocus(): number | undefined {
        if (!this._searchInputWidthOnFocus && this._isSearchSuggestionsEnabled) {
            const inputElement = document.getElementById("l1-search-input");
            const spaceOnRight = inputElement ? window.innerWidth - inputElement.getBoundingClientRect().right - 7 : undefined;
            const helpCalloutWidth = this._isHigherResolution ? 690 : 550;
            this._searchInputWidthOnFocus = spaceOnRight != null ? helpCalloutWidth - spaceOnRight : undefined;
        }

        return this._searchInputWidthOnFocus;
    }

    private _onExecuteSearch = (searchQuery: ISearchQuery): void => {
        const searchService = this.context.pageContext.getService<IVssSearchService>("IVssSearchService");
        searchService.execute(searchQuery);
    };

    private _dispatchEvent = (eventName: string): void => {
        const event = document.createEvent("Event");
        event.initEvent(eventName, false, true);
        window.dispatchEvent(event);
    };

    private _expandSearchBox = (): void => {
        this._publishTelemetry();
        this._dispatchEvent("searchExpanded");

        this.setState({ isExpanded: true }, this._setCursorAtEnd);
    };

    private _setCursorAtEnd = (): void => {
        // Set cursor at the end after search box expands.
        setTimeout(() => {
            if (this._searchInputComponent) {
                this._searchInputComponent.focus(true);
            }
        }, 0);
    };

    private _collapseSearchBox = (shouldClearSelectedProviderId?: boolean): void => {
        this._dispatchEvent("searchCollapsed");

        if (this._searchInputComponent && !this._searchInputComponent.state.searchPrefixProps) {
            // Reset placeholder text if there is no search prefix. It might have been changed by the search provider.
            this._searchInputComponent.setState({ placeholderText: Resources.SearchPlaceholder });
        }
        this.setState({ isExpanded: false, selectedProviderId: shouldClearSelectedProviderId ? undefined : this.state.selectedProviderId });
    };

    private _onBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
        const focusTarget = (event.relatedTarget as HTMLElement) || document.activeElement;
        if (this._searchHeader && !contains(this._searchHeader, focusTarget)) {
            this._collapseSearchBox();
        }
    };

    private _onSearchTabLoaded = (evt: CustomEvent): void => {
        const detail = evt.detail;
        if (detail && detail.providerId) {
            this.setState({ selectedProviderId: detail.providerId, isExpanded: false });
        }
    };

    private registerShortcut(): void {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const displayedNavigation = navigationService.getDisplayedNavigation();
        const selectedHubGroupId = displayedNavigation.length >= 3 ? displayedNavigation[2] : "";

        if (
            selectedHubGroupId !== "ms.vss-search-platform.search-hidden-collection-hub-group" &&
            selectedHubGroupId !== "ms.vss-search-platform.search-hidden-project-hub-group"
        ) {
            const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
            shortcutService.registerShortcut(Resources.Global, {
                combos: ["/", "s"],
                description: Resources.SearchPlaceholder,
                action: () => {
                    if (this._searchInputComponent) {
                        this._searchInputComponent.focus();
                    }
                }
            });
        }
    }

    private _publishTelemetry = (): void => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const displayedNavigation = navigationService.getDisplayedNavigation();
        const selectedHubGroupId = displayedNavigation.length >= 3 ? displayedNavigation[2] : "";
        const selectedHubId = displayedNavigation.length >= 4 ? displayedNavigation[3] : "";

        telemetryService.publishEvent("WebPlatform", "L1SearchBarFocussed", {
            launchPoint_HubGroup: selectedHubGroupId,
            launchPoint_hub: selectedHubId
        });
    };
}

VssComponent.register("expandableSearchHeader", ExpandableSearchBox);
